/**
 * StoreSales
 * --
 */
import { useState, useEffect } from 'react';
import { Content, Title, Row, Col } from 'components';
import { Divider, Button, Table, Radio, Spin, Statistic } from 'antd';
import { ExportOutlined } from '@ant-design/icons';
import {
  CartesianGrid,
  Tooltip,
  BarChart,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Legend,
  Bar,
} from 'recharts';
import MessageAlert, { strCut, stringToMoneyFormat } from 'utils';
import moment from 'moment';
import dayjs from 'dayjs';
import { StatisticsApi, OrderApi } from 'api';

const DAY = -10;
const MOMENT_FORMAT = 'YYYY-MM-DD';
const endDate = moment().format(MOMENT_FORMAT);
const startDate = moment().add(DAY, 'days').format(MOMENT_FORMAT);
const today = moment().format('YYYY-MM-DD(HH:mm:ss)');
const today2 = moment().format('YYYY-MM-DD');

/**----------------------------------------
 * [Component] 홈 > 매장별 매출 차트
 * ----------------------------------------
 */
const StoreSales = ({ data, shopInformationData, isMobile, shopType }) => {
  /* ===== ROUTE ===== */
  /* ===== PROPS ===== */
  /* ===== STATE ===== */
  const [loading, setLoading] = useState(false);
  /* ===== VARIABLE ===== */
  /* ===== FUNCTION ===== */
  const getRaw = (type) => {
    if (type) {
      const list = data
        ? data
            ?.map((item) => ({
              ...item,
              date: dayjs(item.date).format('YYYY.MM.DD'),
            }))
            ?.sort((a, b) => dayjs(a.date).diff(dayjs(b.date)))
        : [];

      return list;
    } else {
      const list = shopInformationData?.reduce((acc, cur) => {
        if (!acc || !acc[cur.shop_information_id]) {
          acc[`${cur.shop_information_id}`] = {
            shop_information_id: cur?.shop_information_id,
            shop_information_name: cur?.shop_information_name ?? '-',
            totalAmount: 0,
            netsAmount: 0,
          };
        }

        acc[cur.shop_information_id]['totalAmount'] += Number(
          cur.totalAmount ?? 0
        );
        acc[cur.shop_information_id]['netsAmount'] += Number(
          cur.netsAmount ?? 0
        );
        return acc;
      }, {});

      return list;
    }
  };

  /* ===== HOOKS ===== */
  /* ===== RENDER ===== */
  const storeListRaw = getRaw(shopType);

  // shopInformationData?.reduce((acc, cur) => {
  //   if (!acc || !acc[cur.shop_information_id]) {
  //     acc[`${cur.shop_information_id}`] = {
  //       shop_information_id: cur?.shop_information_id,
  //       shop_information_name: cur?.shop_information_name ?? '-',
  //       totalAmount: 0,
  //       netsAmount: 0,
  //     };
  //   }

  //   acc[cur.shop_information_id]['totalAmount'] += Number(cur.totalAmount ?? 0);
  //   acc[cur.shop_information_id]['netsAmount'] += Number(cur.netsAmount ?? 0);
  //   return acc;
  // }, {});

  const storeList = Object.keys(storeListRaw)?.map((item) => ({
    ...storeListRaw[item],
  }));

  // JSX: start
  return (
    <>
      <Spin spinning={loading}>
        <Row gutter={[5, 5]}>
          {/* ===== 차트: start ===== */}
          <Col x={isMobile ? 24 : 10}>
            <Content
              maxWidth={'100%'}
              borderRadius={4}
              padding={isMobile ? 10 : 20}
              height={450}
              style={{ overflowX: 'auto' }}
            >
              {/* <Title
                size={5}
                subButton={
                  <Button
                    size="small"
                    onClick={() => window.open('/store/list', '_blank')}
                  >
                    <ExportOutlined /> 매장관리
                  </Button>
                }
              >
                일별 매출 차트
              </Title> */}
              <p size={5}>최근 10일 매출({today}기준)</p>

              <Divider style={{ margin: '18px 0' }} />
              <ResponsiveContainer
                width="100%"
                height={360}
                // minWidth={isMobile && 400}
                style={{ marginTop: 15, overflow: 'visible' }}
              >
                <BarChart
                  width={isMobile ? 'auto' : 500}
                  height={360}
                  layout={shopType ? 'horizontal' : 'vertical'}
                  data={
                    !storeList
                      ? []
                      : storeList?.map((item) => ({
                          ...item,
                          ['매장명']: item?.shop_information_name ?? '',
                          ['총매출']: Number(item?.totalAmount ?? 0),
                          ['순매출']: Number(item?.netsAmount ?? 0),
                        }))
                  }
                  margin={{
                    top: 5,
                    right: 30,
                    left: 20,
                    bottom: 5,
                  }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    type={shopType ? 'category' : 'number'}
                    dataKey={shopType ? 'date' : '총매출'}
                  />
                  <YAxis
                    type={shopType ? 'number' : 'category'}
                    dataKey={shopType ? '총매출' : '매장명'}
                    // tick={{ padding: 10 }} // Tick과 축 사이에 충분한 간격을 두기 위한 padding
                    // width={shopType ? 60 : 120} // shopType이 false일 때 축의 너비를 충분히 확보
                    // margin={{ left: 20 }} // 왼쪽 여백을 추가하여 축과 매장명 간 간격 확보ㄴ
                  />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="총매출" fill="#82ca9d" />
                  <Bar dataKey="순매출" fill="#8884d8" />
                </BarChart>
              </ResponsiveContainer>
            </Content>
          </Col>
          {/* ===== 차트: end ===== */}
          {/* ===== 테이블: start ===== */}
          <Col x={isMobile ? 24 : 14}>
            <Content
              maxWidth={'100%'}
              height={450}
              borderRadius={4}
              // padding={'14px 5px'}
            >
              <Title
                size={5}
                style={{ marginBottom: 12, fontSize: isMobile ? 14 : 16 }}
                subButton={
                  <Button
                    size="small"
                    onClick={() =>
                      window.open('/sales/dashboard/daily', '_blank')
                    }
                  >
                    <ExportOutlined /> 매출관리
                  </Button>
                }
              >
                매장 일별 매출
              </Title>
              <Divider style={{ margin: '12px 0' }} />
              <Table
                bordered
                size="small"
                scroll={{ x: 720, y: 290 }}
                pagination={false}
                dataSource={data ?? []}
                style={{ borderBottom: '1px solid #f1f1f1' }}
                columns={[
                  {
                    title: (
                      <p
                        style={{
                          lineHeight: '135%',
                          fontSize: isMobile ? '0.7em' : '1em',
                        }}
                      >
                        날짜
                      </p>
                    ),
                    dataIndex: 'date',
                    width: isMobile ? 50 : 100,
                    align: 'center',
                    ...(!isMobile && { fixed: 'left' }),
                    render: (v) => (
                      <p
                        style={{
                          lineHeight: '135%',
                          fontSize: isMobile ? '0.7em' : '1em',
                        }}
                      >
                        {moment(v).format(
                          isMobile ? 'MM/DD' : 'YYYY-MM-DD(ddd)'
                        )}
                      </p>
                    ),
                  },
                  {
                    title: (
                      <p
                        style={{
                          lineHeight: '135%',
                          fontSize: isMobile ? '0.7em' : '1em',
                        }}
                      >
                        총매출
                      </p>
                    ),
                    dataIndex: 'totalAmount',
                    width: isMobile ? 50 : 100,
                    align: 'center',
                    render: (value) => (
                      <p
                        style={{
                          lineHeight: '135%',
                          fontSize: isMobile ? '0.7em' : '1em',
                        }}
                      >{`${stringToMoneyFormat(Number(value))}원`}</p>
                    ),
                  },
                  {
                    title: (
                      <p
                        style={{
                          lineHeight: '135%',
                          fontSize: isMobile ? '0.7em' : '1em',
                        }}
                      >
                        순매출
                      </p>
                    ),
                    dataIndex: 'netsAmount',
                    width: isMobile ? 50 : 100,
                    align: 'center',
                    render: (value) => (
                      <p
                        style={{
                          lineHeight: '135%',
                          fontSize: isMobile ? '0.7em' : '1em',
                        }}
                      >{`${stringToMoneyFormat(Number(value))}원`}</p>
                    ),
                  },
                  {
                    title: (
                      <p
                        style={{
                          lineHeight: '135%',
                          fontSize: isMobile ? '0.7em' : '1em',
                        }}
                      >
                        주문건수
                      </p>
                    ),
                    dataIndex: 'paymentCount',
                    width: isMobile ? 50 : 100,
                    align: 'center',
                    render: (value) => (
                      <p
                        style={{
                          lineHeight: '135%',
                          fontSize: isMobile ? '0.7em' : '1em',
                        }}
                      >{`${stringToMoneyFormat(Number(value))}건`}</p>
                    ),
                  },
                  {
                    title: (
                      <p
                        style={{
                          lineHeight: '135%',
                          fontSize: isMobile ? '0.7em' : '1em',
                        }}
                      >
                        할인금액
                      </p>
                    ),
                    dataIndex: 'discountAmount',
                    width: isMobile ? 50 : 100,
                    align: 'center',
                    render: (value) => (
                      <p
                        style={{
                          lineHeight: '135%',
                          fontSize: isMobile ? '0.7em' : '1em',
                        }}
                      >{`${stringToMoneyFormat(Number(value))}원`}</p>
                    ),
                  },
                  {
                    title: (
                      <p
                        style={{
                          lineHeight: '135%',
                          fontSize: isMobile ? '0.7em' : '1em',
                        }}
                      >
                        사용포인트
                      </p>
                    ),
                    dataIndex: 'usePointAmount',
                    width: isMobile ? 50 : 100,
                    align: 'center',
                    render: (value) => (
                      <p
                        style={{
                          lineHeight: '135%',
                          fontSize: isMobile ? '0.7em' : '1em',
                        }}
                      >{`${stringToMoneyFormat(Number(value))}P`}</p>
                    ),
                  },
                ]}
                summary={(row) => {
                  const totalAmount = row.reduce(
                    (acc, cur) => acc + Number(cur.totalAmount),
                    0
                  );
                  const netsAmount = row.reduce(
                    (acc, cur) => acc + Number(cur.netsAmount),
                    0
                  );
                  const paymentCount = row.reduce(
                    (acc, cur) => acc + Number(cur.paymentCount),
                    0
                  );
                  const discountAmount = row.reduce(
                    (acc, cur) => acc + Number(cur.discountAmount),
                    0
                  );
                  const usePointAmount = row.reduce(
                    (acc, cur) => acc + Number(cur.usePointAmount),
                    0
                  );

                  return (
                    <Table.Summary fixed>
                      <Table.Summary.Row align="center">
                        <Table.Summary.Cell index={0}>
                          <b
                            style={{
                              lineHeight: '135%',
                              fontSize: isMobile ? '0.7em' : '1em',
                            }}
                          >
                            합계
                          </b>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={1}>
                          <b
                            style={{
                              lineHeight: '135%',
                              fontSize: isMobile ? '0.7em' : '1em',
                            }}
                          >
                            {stringToMoneyFormat(totalAmount)}
                          </b>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={2}>
                          <b
                            style={{
                              lineHeight: '135%',
                              fontSize: isMobile ? '0.7em' : '1em',
                            }}
                          >
                            {stringToMoneyFormat(netsAmount)}
                          </b>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={3}>
                          <b
                            style={{
                              lineHeight: '135%',
                              fontSize: isMobile ? '0.7em' : '1em',
                            }}
                          >
                            {stringToMoneyFormat(paymentCount)}
                          </b>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={4}>
                          <b
                            style={{
                              lineHeight: '135%',
                              fontSize: isMobile ? '0.7em' : '1em',
                            }}
                          >
                            {stringToMoneyFormat(discountAmount)}
                          </b>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={4}>
                          <b
                            style={{
                              lineHeight: '135%',
                              fontSize: isMobile ? '0.7em' : '1em',
                            }}
                          >
                            {stringToMoneyFormat(usePointAmount)}
                          </b>
                        </Table.Summary.Cell>
                      </Table.Summary.Row>
                    </Table.Summary>
                  );
                }}
              />
            </Content>
          </Col>
          {/* ===== 테이블: end ===== */}
        </Row>
      </Spin>
    </>
  ); // JSX: end
};

StoreSales.defaultProps = {
  onSelect: () => {},
  shopInformationData: [],
};
export default StoreSales;
